import { useEffect, useMemo } from 'react';
import { Settings } from '@weave/schema-gen-ts/dist/schemas/user-preferences/user_preferences.pb';
import { useMutation, useQuery } from 'react-query';
import { UserPreferences1 } from '@frontend/api-user-preferences';
import { http } from '@frontend/fetch';
import { sentry } from '@frontend/tracking';
import { shouldLazyLoad, migrationConfigs, LAZY_LOADED_PREFERENCES } from '../constants';
import { preferenceActions, usePreferenceStore } from '../store/store';
import { PreferenceKey, StoredPreference } from '../types';
import { migrateSinglePreference } from '../utils/migration';

export const usePreferencesBootstrap = () => {
  const isReady = usePreferenceStore((state) => state._internal_isReady);
  const query = UserPreferences1.Queries.useGetUserPreferencesQuery(
    {
      filter: {
        excludedSettings: LAZY_LOADED_PREFERENCES,
      },
    },
    { enabled: isReady }
  );

  const transformedPrefs = useMemo(() => {
    if (!query.data) {
      return;
    }
    const userSettingsMap = query.data?.userSettingsMap || {};
    const result: Record<string, StoredPreference> = {};

    Object.entries(userSettingsMap).forEach(([id, pref]: [string, Settings]) => {
      if (!shouldLazyLoad(id as PreferenceKey)) {
        result[id] = {
          id,
          value: JSON.parse(pref.settingValue || ''),
          updatedAt: pref.updatedAt || '',
        };
      }
    });

    return result;
  }, [query.data]);

  useEffect(() => {
    if (transformedPrefs) {
      preferenceActions.setPreferences(transformedPrefs);
      preferenceActions.setDataInitialized(true);
    }
  }, [transformedPrefs]);

  useEffect(() => {
    if (query?.error) {
      if (http.isHttpError(query?.error) && query?.error?.status === 401) {
        sentry.error({ team: 'fe-ux-platform', error: query.error, topic: 'user-preferences', severityLevel: 'error' });
      }
      preferenceActions.setIsDataError(true);
    }
  }, [query?.error]);

  return {
    data: transformedPrefs,
    isLoading: query.isLoading,
    error: query.error,
  };
};

export const usePreferenceMutation = () => {
  const updatePreferenceMutation = UserPreferences1.Mutations.useUpdateUserPreferencesMutation();
  return useMutation({
    mutationFn: async (preference: { id: string; value: any; updatedAt: string }) => {
      const payload = {
        userSettingsMap: {
          [preference.id]: JSON.stringify(preference.value),
        },
        updatedAt: preference.updatedAt,
        clientId: usePreferenceStore.getState().clientId,
      };
      await updatePreferenceMutation.mutateAsync(payload);
      return preference;
    },
    onError: (error) => {
      if (http.isHttpError(error) && error.status === 401) {
        sentry.error({
          error,
          team: 'fe-ux-platform',
          topic: 'user-preferences',
          severityLevel: 'error',
        });
      }
    },
  });
};

export const usePreferenceBulkMutation = () => {
  const updatePreferenceMutation = UserPreferences1.Mutations.useUpdateUserPreferencesMutation();
  return useMutation({
    mutationFn: async (newUserSettingsMap: Record<string, string>) => {
      const payload = {
        userSettingsMap: { ...newUserSettingsMap },
        updatedAt: new Date().toISOString(),
      };
      await updatePreferenceMutation.mutateAsync(payload);
      return newUserSettingsMap;
    },
  });
};

export const useLazyPreferenceQuery = (preferenceId: PreferenceKey, enabled = true) => {
  const isReady = usePreferenceStore((state) => state._internal_isReady);
  const fetchGetUserPreferences = UserPreferences1.Queries.useFetchGetUserPreferences();
  const bulkMutation = usePreferenceBulkMutation();

  const query = useQuery(
    ['single-preference', preferenceId],
    async () => {
      const response = await fetchGetUserPreferences({
        filter: {
          includedSettings: [preferenceId],
        },
      });
      const userSettingsMap = response?.userSettingsMap || {};
      const pref = userSettingsMap[preferenceId];

      if (pref) {
        const preferenceData = {
          id: preferenceId,
          value: JSON.parse(pref.settingValue || ''),
          updatedAt: pref.updatedAt || '',
        };

        preferenceActions.setPreference(preferenceId, preferenceData.value, preferenceData.updatedAt);
        preferenceActions.setLazyLoaded(preferenceId);
        return preferenceData;
      }

      const migrated = await migrateSinglePreference(preferenceId, migrationConfigs, bulkMutation);

      if (migrated) {
        preferenceActions.setLazyLoaded(preferenceId);
        const storeState = usePreferenceStore.getState();
        return storeState.prefs[preferenceId] || null;
      }

      return null;
    },
    {
      enabled: enabled && isReady,
      staleTime: Infinity,
      cacheTime: 24 * 60 * 60 * 1000,
    }
  );

  useEffect(() => {
    if (query.error) {
      if (http.isHttpError(query.error) && query.error.status === 401) {
        sentry.error({
          team: 'fe-ux-platform',
          error: query.error,
          topic: 'user-preferences',
          severityLevel: 'error',
        });
      }
    }
  }, [query.error]);

  return {
    data: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
};
